import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import AvailableDatePicker from "./AvailableDatePicker";
import { useTranslation } from "react-i18next";
import { dateLocale } from "../i18n";

const computeDatesInRange = (start, end, availableDates) => {
  if (!start || !end) return [];
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const e = new Date(end);
  e.setHours(0, 0, 0, 0);
  if (s > e) return [];
  return availableDates
    .filter((date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d >= s && d <= e;
    })
    .map((d) => d.toISOString().split("T")[0]);
};

const DateRangeDialog = ({
  open,
  onClose,
  availableDates,
  indexMinDate,
  indexMaxDate,
  remainingSlots,
  onAddLayer,
}) => {
  const { t, i18n } = useTranslation();
  const [rangeStartDate, setRangeStartDate] = useState(null);
  const [rangeEndDate, setRangeEndDate] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [selectedRangeDates, setSelectedRangeDates] = useState(new Set());
  const [rangeExceedsLimit, setRangeExceedsLimit] = useState(false);

  // availableDates is new on every parent render and would reset the picks.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setRangeStartDate(indexMinDate || null);
      setRangeEndDate(indexMaxDate || null);
      const initialDates = computeDatesInRange(
        indexMinDate,
        indexMaxDate,
        availableDates,
      );
      setSelectedRangeDates(new Set(initialDates));
    }
    wasOpen.current = open;
  }, [open, indexMinDate, indexMaxDate, availableDates]);

  const datesInRange = computeDatesInRange(
    rangeStartDate,
    rangeEndDate,
    availableDates,
  );
  const selectedDatesCount = selectedRangeDates.size;
  const rangeInvalid =
    !rangeStartDate ||
    !rangeEndDate ||
    new Date(rangeStartDate) > new Date(rangeEndDate) ||
    selectedDatesCount === 0;

  useEffect(() => {
    setRangeExceedsLimit(selectedDatesCount > remainingSlots);
  }, [selectedDatesCount, remainingSlots]);

  const handleToggleDate = (dateStr) => {
    setSelectedRangeDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) {
        next.delete(dateStr);
      } else {
        next.add(dateStr);
      }
      return next;
    });
  };

  const handleStartDateChange = (date) => {
    if (date) {
      const selectedDate = new Date(date);
      selectedDate.setHours(12, 0, 0, 0);
      setRangeStartDate(selectedDate);
      const newDates = computeDatesInRange(
        selectedDate,
        rangeEndDate,
        availableDates,
      );
      setSelectedRangeDates(new Set(newDates));
    }
  };

  const handleEndDateChange = (date) => {
    if (date) {
      const selectedDate = new Date(date);
      selectedDate.setHours(12, 0, 0, 0);
      setRangeEndDate(selectedDate);
      const newDates = computeDatesInRange(
        rangeStartDate,
        selectedDate,
        availableDates,
      );
      setSelectedRangeDates(new Set(newDates));
    }
  };

  const handleSubmitRange = async () => {
    if (rangeInvalid || rangeExceedsLimit) return;
    setRangeLoading(true);
    let hadFailure = false;
    try {
      for (const dateStr of selectedRangeDates) {
        const success = await onAddLayer(dateStr);
        if (!success) {
          hadFailure = true;
          break;
        }
      }
    } finally {
      setRangeLoading(false);
    }
    if (!hadFailure) {
      handleClose();
    }
  };

  // No reset needed
  const handleClose = () => {
    if (rangeLoading) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t("map.range.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            {t("map.range.start")}
          </Typography>
          <AvailableDatePicker
            availableDates={availableDates}
            value={rangeStartDate}
            cleanable={false}
            style={{ width: "100%" }}
            onChange={handleStartDateChange}
          />

          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            {t("map.range.end")}
          </Typography>
          <AvailableDatePicker
            availableDates={availableDates}
            value={rangeEndDate}
            cleanable={false}
            style={{ width: "100%" }}
            onChange={handleEndDateChange}
          />

          {rangeStartDate && rangeEndDate && (
            <>
              {new Date(rangeStartDate) > new Date(rangeEndDate) ? (
                <Typography
                  variant="caption"
                  sx={{ color: "error.main", textAlign: "center" }}
                >
                  {t("map.range.startAfterEnd")}
                </Typography>
              ) : (
                <>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {t("map.range.selected", { count: selectedDatesCount })}
                    </Typography>
                    <br />
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {t("map.range.remaining", { count: remainingSlots })}
                    </Typography>
                    {!rangeLoading && rangeExceedsLimit && (
                      <Typography
                        variant="caption"
                        sx={{ color: "error.main", display: "block", mt: 0.5 }}
                      >
                        {t("map.range.notEnough")}
                      </Typography>
                    )}
                    {datesInRange.length === 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: "error.main", display: "block", mt: 0.5 }}
                      >
                        {t("map.range.noDates")}
                      </Typography>
                    )}
                  </Box>

                  {datesInRange.length > 0 && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", gap: 2 }}
                    >
                      <Button
                        size="small"
                        onClick={() =>
                          setSelectedRangeDates(new Set(datesInRange))
                        }
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        {t("map.range.selectAll")}
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          setSelectedRangeDates((prev) => {
                            const next = new Set(prev);
                            datesInRange.forEach((d) => next.delete(d));
                            return next;
                          })
                        }
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        {t("map.range.unselectAll")}
                      </Button>
                    </Box>
                  )}

                  {datesInRange.length > 0 && (
                    <Box
                      sx={{
                        maxHeight: 220,
                        overflow: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      {datesInRange.map((dateStr) => (
                        <Box
                          key={dateStr}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Checkbox
                            checked={selectedRangeDates.has(dateStr)}
                            onChange={() => handleToggleDate(dateStr)}
                            size="small"
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.8rem" }}
                          >
                            {new Date(dateStr).toLocaleDateString(
                              dateLocale(i18n.resolvedLanguage),
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={rangeLoading}
          color="inherit"
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitRange}
          disabled={rangeInvalid || rangeExceedsLimit || rangeLoading}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {rangeLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            t("map.range.add", { count: selectedDatesCount })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DateRangeDialog;
